import { useState } from "react";

import { useDeepCompareEffect } from "use-deep-compare";

import { isTagCriteria } from "../utils/criteriaFilterUtils";

import type { CriteriaFilterValue } from "@stacklet/ui";

function updateCriteriaFilterValues(
  criteriaFilterValue: CriteriaFilterValue,
  existingValues: CriteriaFilterValue[],
) {
  const isTag = isTagCriteria(criteriaFilterValue.criteria);
  const existingIndex = existingValues.findIndex((existing) => {
    const criteriaMatch = existing.criteria === criteriaFilterValue.criteria;
    const operatorMatch = existing.operator === criteriaFilterValue.operator;
    // For tag criteria, also compare the name field (tag key) for unique identification
    const nameMatch = isTag ? existing.name === criteriaFilterValue.name : true;
    return criteriaMatch && operatorMatch && nameMatch;
  });

  if (existingIndex !== -1) {
    // Replace existing element with the same criteria, operator combination
    // (and name for tag criteria)
    const updatedValues = [...existingValues];
    updatedValues[existingIndex] = criteriaFilterValue;
    return updatedValues;
  }

  // Add new criteria filter value if none exists with that criteria, operator combination
  // (and name for tag criteria)
  return [...existingValues, criteriaFilterValue];
}

interface Props {
  handleSelectionsChange?: (selections: CriteriaFilterValue[]) => void;
  /** Optional initial criteria filters to restore state */
  initialCriteriaFilters?: CriteriaFilterValue[];
  /** Optional default selections when no criteria filters exist */
  defaultSelections?: string[];
}

export function useSteppedMultiColumnDropdown({
  handleSelectionsChange,
  initialCriteriaFilters = [],
  defaultSelections,
}: Props) {
  const initialSelections = defaultSelections ?? [];

  // Initialize state from initialCriteriaFilters if provided, otherwise empty array
  const [criteriaFilters, setCriteriaFilters] = useState<CriteriaFilterValue[]>(
    initialCriteriaFilters,
  );

  // Sync local state with initialCriteriaFilters when it changes externally (e.g., when navigating back to step)
  useDeepCompareEffect(() => {
    setCriteriaFilters(initialCriteriaFilters);
  }, [initialCriteriaFilters]);

  function handleChange(criterion: CriteriaFilterValue): void {
    const updatedValues = updateCriteriaFilterValues(
      criterion,
      criteriaFilters,
    );

    setCriteriaFilters(updatedValues);
    handleSelectionsChange?.(updatedValues);
  }

  function handleRemove(criterion: CriteriaFilterValue): void {
    // Update criteria values, by removing the criteria filter value with the same criteria, operator combination
    // (and name for tag criteria)
    const isTag = isTagCriteria(criterion.criteria);

    const updatedValues = criteriaFilters.filter((val) => {
      const criteriaMatch = val.criteria === criterion.criteria;
      const operatorMatch = val.operator === criterion.operator;
      // For tag criteria, also compare the name field (tag key) for unique identification
      const nameMatch = isTag ? val.name === criterion.name : true;
      // Keep entries that don't match all three (criteria, operator, and name for tags)
      return !(criteriaMatch && operatorMatch && nameMatch);
    });

    setCriteriaFilters(updatedValues);
    handleSelectionsChange?.(updatedValues);
  }

  return {
    handleChange,
    handleRemove,
    criteriaFilters,
    initialSelections,
  };
}
